import type {
  AuditLogEntry as GQLItem,
  AuditLogEntryConnection as GQLConnection,
  AuditLogVerb,
} from '../../../generated/api.graphql';
import { take } from '../../../util';
import * as GqlConnection from '../../connection/GqlConnection';
import * as PageInfo from '../../connection/PageInfo';

// A string union rather than the generated enum, which predates the backend's `ownership_taken`.
export type AuditLogChange = 'secret_rotated' | 'delivery_switched' | 'ownership_taken';

export type AuditLogUpdate = {
  field: string;
  from: unknown;
  to: unknown;
};

/** One recorded change: who did what, when, and to which fields. */
export type AuditLogEntry = {
  id: number;
  createdAt: string;
  verb: AuditLogVerb;
  change: AuditLogChange | null;
  actorName: string | null;
  updates: AuditLogUpdate[];
};

export const fromGql = (item: GQLItem): AuditLogEntry => ({
  ...take<AuditLogEntry>(item, 'id', 'createdAt', 'verb'),
  change: (item.change as AuditLogChange | null | undefined) ?? null,
  actorName: item.actor?.name ?? null,
  updates: item.updates
    .filter(({ field }) => !/secret/i.test(field))
    .map(({ field, from, to }) => ({ field, from, to })),
});

/** `edges` and `node` are nullable on the schema, so the shared `GqlConnection.fromGql` does not fit. */
export const fromGqlConnection = (connection: GQLConnection): GqlConnection.GqlConnection<AuditLogEntry> => {
  const edges = (connection.edges ?? [])
    .filter(edge => Boolean(edge?.node))
    .map(edge => ({ cursor: edge!.cursor, node: fromGql(edge!.node as GQLItem) }));

  return {
    edges,
    nodes: edges.map(edge => edge.node),
    pageInfo: PageInfo.fromGql(connection.pageInfo),
  };
};
